/**
 * Contact page
 * ------------
 * Public marketing page. Wires inbound to /api/inbound/contact which logs
 * to `contact_messages` and emails support@bloq.video. Postmark inbound
 * receiver is `/api/inbound/postmark` — separate path, used by Postmark's
 * own webhook (see the "How to configure Postmark inbound" doc in
 * /app/POSTMARK_INBOUND_SETUP.md).
 *
 * Visual: social icons live HERE (moved out of MarketingNav) so the
 * marketing header stays minimal and the contact page becomes the canonical
 * place to reach Bloq.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import SocialLinks from "@/components/SocialLinks";
import api, { formatApiError } from "@/lib/api";
import { Mail, MessageCircle, Phone, MapPin, ShieldAlert } from "lucide-react";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !msg.trim()) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setBusy(true);
    try {
      await api.post("/inbound/contact", {
        name: name.trim(),
        email: email.trim(),
        message: msg.trim(),
        source: "marketing-contact",
      });
      toast.success("Thanks! We'll catch up with you soon.");
      setName("");
      setEmail("");
      setMsg("");
    } catch (err) {
      toast.error(
        formatApiError(err) ||
          "Could not send your message — please email us directly at findus@bloq.video"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />
      <section className="bloq-section">
        <div className="bloq-container grid lg:grid-cols-2 gap-14">
          <div>
            <p className="bloq-eyebrow">Contact</p>
            <h1 className="bloq-display text-4xl sm:text-5xl lg:text-6xl mt-3 mb-5">
              We read every message. <span className="bloq-gradient-text">Really.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#4a4a52] leading-relaxed mb-10">
              Want to chat about your workflow, your team, or a feature you wish existed?
              Send us a note — we'll catch up with you soon.
            </p>

            <div className="flex flex-col gap-5">
              {[
                {
                  icon: Mail,
                  t: "findus@bloq.video",
                  d: "General — best for everyone.",
                  href: "mailto:findus@bloq.video",
                },
                {
                  icon: MessageCircle,
                  t: "support@bloq.video",
                  d: "Logged-in users: this gets the fastest reply.",
                  href: "mailto:support@bloq.video",
                },
                {
                  icon: Phone,
                  t: "(844) 700-BLOQ (2567)",
                  d: "",
                  href: "tel:+18447002567",
                },
                {
                  icon: MapPin,
                  t: "Proudly Created in Colorado",
                  d: "Bloq is a Remote-First Venture.",
                },
              ].map((r, i) => {
                const I = r.icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3"
                    data-testid={`contact-info-row-${i}`}
                  >
                    <I className="w-5 h-5 mt-1 text-[#005e7a] flex-shrink-0" />
                    <div className="min-w-0">
                      {r.href ? (
                        <a href={r.href} className="font-medium text-[#1a1a1f] hover:text-[#005e7a] break-all">
                          {r.t}
                        </a>
                      ) : (
                        <p className="font-medium break-words">{r.t}</p>
                      )}
                      {r.d && <p className="text-sm text-[#82828c]">{r.d}</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social icons moved here from the marketing nav. */}
            <div className="mt-8 pt-6 border-t border-[#e5e0d6]" data-testid="contact-social-block">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#82828c] font-medium mb-3">
                Follow along
              </p>
              <SocialLinks tone="light" />
              <Link
                to="/beta-disclaimer"
                className="mt-5 inline-flex items-center gap-2 text-xs text-[#b45309] font-medium hover:underline"
                data-testid="contact-beta-disclaimer-link"
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                Read the Bloq beta disclaimer
              </Link>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="bloq-glass-strong p-6 sm:p-7 flex flex-col gap-4 h-fit"
            data-testid="contact-form"
          >
            <p className="bloq-headline text-xl sm:text-2xl">Drop us a line</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bloq-input mt-1"
                data-testid="contact-name-input"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bloq-input mt-1"
                data-testid="contact-email-input"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Message
              </label>
              <textarea
                rows={5}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                className="bloq-input mt-1"
                data-testid="contact-message-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="bloq-btn-primary mt-3"
              data-testid="contact-submit-button"
            >
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
